// ADR-0012 §2.1 / Fatia 3 Wave 2 (Trilha 3) — decrypt 2 passos do membro.
//
// Padrão ESCOPADO (decisão travada): withMemberDek(material, fn) faz os 2 passos,
// entrega o DEK reaberto ao callback `fn`, e ZERA o DEK num finally DENTRO da lib.
// O DEK NUNCA é retornado — o retorno é o resultado de `fn` (o plaintext legítimo
// que a Wave 3 quer ler). A lib não retém o DEK em lugar nenhum (sem var
// module-level, sem storage, sem fetch, sem log).
//
// Roda SÓ no browser do membro (apps/web-comite). R-2.6: esta é a zona AUTORIZADA
// (com a CaseDetailPage da Wave 3) a chamar unwrapMemberPrivateKey /
// unwrapDekWithMemberPrivate. O anonymity-grep BLOQUEIA esses símbolos em
// apps/api/src + apps/web-admin/src.

import { unwrapMemberPrivateKey, unwrapDekWithMemberPrivate } from '@aions/crypto';
import { base64ToBytes } from './bytes.js';

/**
 * Material necessário pra reabrir o DEK do membro (ADR-0012 §2.1). Forma travada
 * pelo plano: tudo o que vem do GET do caso (Wave 1) + a KEK do caller (Wave 3).
 */
export interface MemberDekMaterial {
  /** b64 — comitePrivateKeyCiphertext (= myWrappedPrivateKey.ciphertext da Wave 1). */
  privateKeyCiphertext: string;
  /** b64 — comitePrivateKeyNonce (= myWrappedPrivateKey.nonce da Wave 1). */
  privateKeyNonce: string;
  /** b64 — dekWrappedByMembers[meuId].wrappedDek (wrap RSA da Fatia 2). */
  wrappedDek: string;
  /** KEK_membro de 32 bytes (loadKekMembro — fornecida pelo CALLER na Wave 3). */
  kekRaw: Uint8Array;
}

/**
 * Decrypt 2 passos do membro (ADR-0012 §2.1, Fatia 3). Padrão ESCOPADO: a lib
 * faz os 2 passos, entrega o DEK ao callback `fn`, e ZERA o DEK num finally —
 * o DEK NUNCA é retornado nem retido. Roda SÓ no browser do membro (web-comite).
 *
 * Higiene de memória (fronteira travada):
 *  - o DEK criado aqui → dekRaw.fill(0) no finally (roda mesmo se fn lançar);
 *  - m.kekRaw é INPUT do CALLER → a lib NUNCA a muta/zera (o caller a zera);
 *  - a privada é CryptoKey NÃO-EXTRAÍVEL → GC cuida (sem bytes em claro).
 *
 * NÃO faz fetch, NÃO persiste (sessionStorage), NÃO usa var module-level, NÃO
 * loga segredo — invariante dos 2 browsers (ADR-0012 §2.4): o DEK em claro vive
 * só neste escopo, no browser do membro, e morre no finally. Nada volta pro servidor.
 *
 * R-2.6: este arquivo é a ÚNICA zona (com a CaseDetailPage da Wave 3) autorizada
 * a chamar unwrapMemberPrivateKey/unwrapDekWithMemberPrivate. O grep
 * anonymity-grep.sh BLOQUEIA esses símbolos em apps/api + apps/web-admin.
 */
export async function withMemberDek<T>(
  m: MemberDekMaterial,
  fn: (dekRaw: Uint8Array) => Promise<T>,
): Promise<T> {
  // passo 1 — destranca a PRÓPRIA privada sob a KEK_membro (importada não-extraível
  // pelo primitivo da Fatia 1). KEK errada → AES-GCM tag fail aqui (fn não roda).
  const privKey = await unwrapMemberPrivateKey(m.privateKeyCiphertext, m.privateKeyNonce, m.kekRaw);
  // passo 2 — reabre o DEK com a privada (Uint8Array fresco, 32 bytes, efêmero).
  // wrap de outro membro → RSA-OAEP unwrap falha aqui (fn não roda).
  const dekRaw = await unwrapDekWithMemberPrivate(base64ToBytes(m.wrappedDek), privKey);
  // passo 3 — entrega ao callback; ZERA o DEK no finally (mesmo se fn lançar).
  // A KEK do caller (m.kekRaw) NÃO é tocada — fronteira de propriedade travada.
  try {
    return await fn(dekRaw);
  } finally {
    dekRaw.fill(0);
  }
}
